import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  useMediaQuery,
  Paper,
  Divider,
} from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ProductLabel from './ProductLabel';
import { printProductLabels } from './printLabelEngine';
import { isBarcodeSupported } from './BarcodeSVG';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      borderRadius: 0,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  previewContainer: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    overflow: 'hidden',
  },
  qtyButton: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: 6,
    minWidth: 38,
    height: 38,
  },
  qtyInput: {
    width: 65,
    margin: '0 8px',
    '& input': {
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '1.1rem',
    },
  },
  chipSelected: {
    backgroundColor: '#17497F !important',
    color: '#ffffff !important',
    fontWeight: 'bold',
  },
}));

const ProductSinglePrintModal = ({ open, handleClose, product }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [quantity, setQuantity] = useState(1);
  const [selectedParticulars, setSelectedParticulars] = useState([]);
  const [customTopText, setCustomTopText] = useState('');
  const [printFormat, setPrintFormat] = useState('thermal');
  const [showCutLine, setShowCutLine] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showLateralId, setShowLateralId] = useState(true);
  const [codeType, setCodeType] = useState('barcode'); // 'barcode' | 'qrcode'

  const barcodeValue = String(product?.barCode || product?.id || '').trim();
  const barcodeSupported = useMemo(() => isBarcodeSupported(barcodeValue), [barcodeValue]);

  // Parse product particularities keys
  const availableParticulars = useMemo(() => {
    if (!product?.particulars) return [];
    let parsed = product.particulars;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        parsed = {};
      }
    }
    if (!parsed || typeof parsed !== 'object') return [];
    return Object.entries(parsed).map(([key, val]) => ({
      key,
      label: key.toUpperCase(),
      value: String(val),
    }));
  }, [product]);

  // Reset/initialize state when modal opens
  useEffect(() => {
    if (open && product) {
      setQuantity(1);
      setCustomTopText('');
      setPrintFormat('thermal');
      setShowCutLine(true);
      setShowPrice(true);
      setShowLateralId(true);

      const codeVal = String(product?.barCode || product?.id || '').trim();
      if (!isBarcodeSupported(codeVal)) {
        setCodeType('qrcode');
      } else {
        setCodeType('barcode');
      }

      // By default select all available particularities
      if (availableParticulars.length > 0) {
        setSelectedParticulars(availableParticulars.map((p) => p.key));
      } else {
        setSelectedParticulars([]);
      }
    }
  }, [open, product, availableParticulars]);

  const handleToggleParticular = (key) => {
    setSelectedParticulars((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleQtyChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  const handleUseStockQty = () => {
    const stock = parseInt(product?.amount, 10);
    if (!Number.isNaN(stock) && stock > 0) {
      setQuantity(stock);
    }
  };

  const handlePrint = () => {
    printProductLabels(
      [
        {
          product,
          quantity,
          selectedParticulars,
          customTopText,
          codeType,
        },
      ],
      {
        format: printFormat,
        codeType,
        showCutLine,
        showLateralId,
        showPrice,
      },
    );
    setTimeout(() => {
      handleClose();
    }, 300);
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon style={{ color: '#17497F', marginRight: 8 }} />
          <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Imprimir Etiqueta do Produto
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '20px' }}>
        <Grid container spacing={2}>
          {/* Seletor de Quantidade */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Quantidade de Etiquetas
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Estoque atual: {product.amount || 0} un
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <IconButton
                  size="small"
                  className={classes.qtyButton}
                  onClick={() => handleQtyChange(-1)}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <TextField
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className={classes.qtyInput}
                  inputProps={{ min: 1 }}
                  size="small"
                  variant="outlined"
                />
                <IconButton
                  size="small"
                  className={classes.qtyButton}
                  onClick={() => handleQtyChange(1)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                {Number(product.amount) > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ marginLeft: 8, textTransform: 'none' }}
                    onClick={handleUseStockQty}
                  >
                    Estoque ({product.amount})
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Seleção de Particularidades (com suporte a múltiplas seleções) */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>
              Particularidades no Topo da Etiqueta
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
              Selecione uma ou mais particularidades para compor o cabeçalho superior:
            </Typography>

            {availableParticulars.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {availableParticulars.map((p) => {
                  const isSelected = selectedParticulars.includes(p.key);
                  return (
                    <Chip
                      key={p.key}
                      label={`${p.label}: ${p.value}`}
                      onClick={() => handleToggleParticular(p.key)}
                      clickable
                      color={isSelected ? 'primary' : 'default'}
                      className={isSelected ? classes.chipSelected : undefined}
                      style={{ margin: 3 }}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Este produto não possui particularidades cadastradas (exibirá ID/Referência no topo).
              </Typography>
            )}

            {/* Campo para texto customizado opcional */}
            <TextField
              label="Ou informe um texto personalizado para o topo"
              placeholder="Ex: TAM: 37 • COR: PRETO"
              value={customTopText}
              onChange={(e) => setCustomTopText(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: 12 }}
            />
          </Grid>

          {/* Pré-visualização da Etiqueta */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
              Pré-visualização da Etiqueta
            </Typography>
            <Paper className={classes.previewContainer} elevation={0}>
              <ProductLabel
                product={product}
                selectedParticulars={selectedParticulars}
                customTopText={customTopText}
                codeType={codeType}
                showCutLine={showCutLine}
                showPrice={showPrice}
                showLateralId={showLateralId}
                scale={1}
              />
            </Paper>
          </Grid>

          {/* Opções de Tipo de Código (Código de Barras vs QR Code) */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <FormControl component="fieldset">
                <FormLabel component="legend" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Padrão do Código
                </FormLabel>
                <RadioGroup
                  row
                  value={codeType}
                  onChange={(e) => setCodeType(e.target.value)}
                >
                  <FormControlLabel
                    value="barcode"
                    disabled={!barcodeSupported}
                    control={<Radio size="small" color="primary" />}
                    label={
                      <Typography variant="body2" style={{ color: !barcodeSupported ? '#9ca3af' : undefined }}>
                        Código de Barras
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="qrcode"
                    control={<Radio size="small" color="primary" />}
                    label={<Typography variant="body2">QR Code</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Grid>

          {/* Opções de Formato e Exibição */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <FormControl component="fieldset">
                <FormLabel component="legend" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Formato de Impressão
                </FormLabel>
                <RadioGroup
                  row
                  value={printFormat}
                  onChange={(e) => setPrintFormat(e.target.value)}
                >
                  <FormControlLabel
                    value="thermal"
                    control={<Radio size="small" color="primary" />}
                    label={<Typography variant="body2">Rolo Térmico (Bobina)</Typography>}
                  />
                  <FormControlLabel
                    value="a4"
                    control={<Radio size="small" color="primary" />}
                    label={<Typography variant="body2">Folha A4 (Grade)</Typography>}
                  />
                </RadioGroup>
              </FormControl>

              <Box display="flex" alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showCutLine}
                      onChange={(e) => setShowCutLine(e.target.checked)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">Picote ✂</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPrice}
                      onChange={(e) => setShowPrice(e.target.checked)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">Preço</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showLateralId}
                      onChange={(e) => setShowLateralId(e.target.checked)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">ID Lateral</Typography>}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', backgroundColor: '#fbfcfd', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          disableElevation
          style={{ fontWeight: 700 }}
        >
          Imprimir {quantity} {quantity === 1 ? 'Etiqueta' : 'Etiquetas'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSinglePrintModal;
